/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { Button, Text } from '@/shared/ui';
import { FF_APP_INSTAGRAM_NATIVE } from '@/shared/utils/feature-flag';
import { setMetaMinDailyBudgetCents } from '@/shared/utils/ad-dialog-helpers';
import {
  API_BASE,
  apiRequest,
  shapeSettings,
  fetchInstagramAuthorizationUrl,
  disconnectInstagramNative,
  type FacebookSettings,
} from './FacebookSettingsSection/facebook-settings.helpers';
import { useFacebookSettingsMockStore, FACEBOOK_MOCK_SCENARIOS } from './FacebookSettingsSection/facebook-settings-mock-store';
import { Content } from './FacebookSettingsSection/Content';
import { BlockMid, SettingsBlock } from './blocks';

/** sessionStorage key remembering a "Facebook only" click across the OAuth redirect round-trip. */
const FACEBOOK_ONLY_INTENT_KEY = 'housien:fb-connect-mode';
/** sessionStorage key for the dev-cockpit connection scenario simulator (see facebook-settings-mock-store.ts). */
const MOCK_SCENARIO_KEY = 'dev:fb-settings-scenario';

export const FacebookSettingsSection: React.FC = () => {
  const { t } = useTranslation('features/agency-settings');
  const agencyId = useAgency((s) => s.agency?.id);
  const [settings, setSettings] = useState<FacebookSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [igConnecting, setIgConnecting] = useState(false);
  const [igDisconnecting, setIgDisconnecting] = useState(false);
  const [showIgDisconnectConfirm, setShowIgDisconnectConfirm] = useState(false);
  const [showIgViaPageDisconnectConfirm, setShowIgViaPageDisconnectConfirm] = useState(false);
  const mockScenario = useFacebookSettingsMockStore((s) => s.scenario);
  const setMockScenario = useFacebookSettingsMockStore((s) => s.setScenario);

  const fetchSettings = useCallback(async (): Promise<FacebookSettings | null> => {
    if (!agencyId) return null;
    if (mockScenario !== 'api' && mockScenario in FACEBOOK_MOCK_SCENARIOS) {
      const mock = FACEBOOK_MOCK_SCENARIOS[mockScenario as keyof typeof FACEBOOK_MOCK_SCENARIOS];
      const shaped = { ...mock, agency_id: agencyId };
      setSettings(shaped);
      setLoading(false);
      return shaped;
    }
    setLoading(true);
    try {
      const raw = await apiRequest(`/portal-integration/agencies/${agencyId}/settings`, 'GET');
      setMetaMinDailyBudgetCents(raw?.ad_min_daily_budget_cents);
      const shaped = shapeSettings(raw, agencyId);
      setSettings(shaped);
      setLoadError(false);
      return shaped;
    } catch {
      // A failed request (network/anti-bot/5xx) is not the same thing as
      // "nothing connected" -- the backend already returns defaults with a
      // normal 200 for a brand-new agency, so this catch only ever fires on
      // a real failure. Falling back to DEFAULTS here used to silently show
      // the onboarding chooser even right after a successful OAuth connect,
      // whenever the follow-up refetch itself failed. Surface the error
      // instead and keep whatever settings we last knew to be true.
      setLoadError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [agencyId, mockScenario]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Captured once, synchronously, on mount -- before agencyId is necessarily
  // resolved -- so the redirect params are stripped from the URL immediately
  // but the toast/refetch below can still wait for a real agencyId instead
  // of running against the stale (agencyId === undefined) closure that
  // existed at mount time.
  const [oauthResult] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('fb_status');
    const message = params.get('fb_message');
    if (!status) return null;
    params.delete('fb_status');
    params.delete('fb_message');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    return { status, message };
  });
  const oauthResultHandled = useRef(false);

  const [igOauthResult] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('ig_status');
    const message = params.get('ig_message');
    if (!status) return null;
    params.delete('ig_status');
    params.delete('ig_message');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    return { status, message };
  });
  const igOauthResultHandled = useRef(false);

  // A real OAuth round-trip (fb_status/ig_status present) is unambiguous
  // proof the user wants their real connection state. Drop any dev-cockpit
  // scenario override left over in this tab from a previous simulation --
  // otherwise a stale "disconnected" mock silently masks a successful
  // connect (fetchSettings() would keep returning the mock forever).
  useEffect(() => {
    if ((oauthResult || igOauthResult) && mockScenario !== 'api') {
      setMockScenario('api');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSettings = useCallback(async (updates: Partial<FacebookSettings>) => {
    if (!agencyId) return;
    setSettings((prev) => prev ? { ...prev, ...updates } : prev);
    try {
      const raw = await apiRequest(`/portal-integration/agencies/${agencyId}/settings`, 'PUT', updates);
      setSettings((prev) => prev ? { ...prev, ...shapeSettings(raw, agencyId) } : prev);
    } catch {
      notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('portal.notifications.erroSalvar'), type: 'error' });
      await fetchSettings();
    }
  }, [agencyId, t, fetchSettings]);

  useEffect(() => {
    if (!oauthResult || !agencyId || oauthResultHandled.current) return;
    oauthResultHandled.current = true;
    if (oauthResult.status === 'success') {
      notifications.show({ title: t('facebook.conectadoSucesso'), message: oauthResult.message ?? '', type: 'success' });
      const intent = sessionStorage.getItem(FACEBOOK_ONLY_INTENT_KEY);
      sessionStorage.removeItem(FACEBOOK_ONLY_INTENT_KEY);
      fetchSettings().then((fresh) => {
        if (intent === 'facebook_only' && fresh) {
          updateSettings({ enabled_portals: fresh.enabled_portals.filter((p) => p !== 'instagram') });
        }
      });
    } else {
      sessionStorage.removeItem(FACEBOOK_ONLY_INTENT_KEY);
      notifications.show({ title: t('facebook.erroConexao'), message: oauthResult.message ?? '', type: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthResult, agencyId, fetchSettings]);

  useEffect(() => {
    if (!igOauthResult || !agencyId || igOauthResultHandled.current) return;
    igOauthResultHandled.current = true;
    if (igOauthResult.status === 'success') {
      notifications.show({ title: t('instagramNative.conectadoSucesso'), message: igOauthResult.message ?? '', type: 'success' });
      fetchSettings();
    } else {
      notifications.show({ title: t('instagramNative.erroConexao'), message: igOauthResult.message ?? '', type: 'error' });
    }
  }, [igOauthResult, agencyId, fetchSettings, t]);

  const connectFacebook = () => {
    if (!agencyId) return;
    sessionStorage.removeItem(FACEBOOK_ONLY_INTENT_KEY);
    window.location.href = `${API_BASE}/portal-integration/facebook/auth?agency_id=${encodeURIComponent(agencyId)}`;
  };

  const connectFacebookOnly = () => {
    if (!agencyId) return;
    sessionStorage.setItem(FACEBOOK_ONLY_INTENT_KEY, 'facebook_only');
    window.location.href = `${API_BASE}/portal-integration/facebook/auth?agency_id=${encodeURIComponent(agencyId)}`;
  };

  const disconnectFacebook = async () => {
    if (!agencyId) return;
    // null (not undefined) so axios actually serializes the field and the
    // backend's dict.update() overwrites it -- an omitted key leaves the
    // stored value untouched. ig_account_id/ig_account_name are cleared here
    // too: that Instagram connection only exists because of this Page, so it
    // cannot survive the Page disconnecting.
    await updateSettings({
      fb_connected: false,
      fb_page_id: null,
      fb_page_name: null,
      fb_ad_account_id: null,
      fb_token_expires_at: null,
      ig_account_id: null,
      ig_account_name: null,
    });
  };

  const connectInstagram = async () => {
    if (!agencyId) return;
    setIgConnecting(true);
    try {
      const authorizationUrl = await fetchInstagramAuthorizationUrl(agencyId);
      if (authorizationUrl) {
        window.location.href = authorizationUrl;
        return;
      }
      notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('instagramNative.erroConexao'), type: 'error' });
    } catch {
      notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('instagramNative.erroConexao'), type: 'error' });
    } finally {
      setIgConnecting(false);
    }
  };

  const disconnectInstagram = async () => {
    if (!agencyId) return;
    setIgDisconnecting(true);
    try {
      await disconnectInstagramNative(agencyId);
      setShowIgDisconnectConfirm(false);
      await fetchSettings();
    } catch {
      notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('portal.notifications.erroSalvar'), type: 'error' });
    } finally {
      setIgDisconnecting(false);
    }
  };

  const togglePortal = (portal: 'facebook' | 'instagram') => {
    if (!settings) return;
    const next = settings.enabled_portals.includes(portal)
      ? settings.enabled_portals.filter((p) => p !== portal)
      : [...settings.enabled_portals, portal];
    updateSettings({ enabled_portals: next });
  };

  const isFbEnabled = settings?.enabled_portals.includes('facebook') ?? false;
  const isIgEnabled = settings?.enabled_portals.includes('instagram') ?? false;

  if (loading) return <div className="text-sm text-(--text-tertiary)">{t('portal.loading')}</div>;

  if (!settings) {
    if (!loadError) return null;
    return (
      <SettingsBlock testId="facebook-settings-block">
        <BlockMid>
          <div className="flex flex-col items-center gap-(--space-3) text-center">
            <Text className="m-0 text-sm text-(--text-secondary)">{t('portal.erroCarregarConexoes')}</Text>
            <Button variant="outlined" size="sm" data-testid="facebook-settings-retry-btn" onClick={() => fetchSettings()}>
              {t('portal.tentarNovamente')}
            </Button>
          </div>
        </BlockMid>
      </SettingsBlock>
    );
  }

  return (
    <Content
      agencyId={agencyId ?? ''}
      settings={settings}
      isFbEnabled={isFbEnabled}
      isIgEnabled={isIgEnabled}
      showDisconnectConfirm={showDisconnectConfirm}
      showIgNativeDisconnectConfirm={showIgDisconnectConfirm}
      igNativeDisconnecting={igDisconnecting}
      onTogglePortal={togglePortal}
      onUpdateSettings={updateSettings}
      onDisconnect={disconnectFacebook}
      onShowDisconnect={() => setShowDisconnectConfirm(true)}
      onCancelDisconnect={() => setShowDisconnectConfirm(false)}
      onChangePage={connectFacebook}
      onDisconnectIgNative={disconnectInstagram}
      onShowIgNativeDisconnect={() => setShowIgDisconnectConfirm(true)}
      onCancelIgNativeDisconnect={() => setShowIgDisconnectConfirm(false)}
      onDisconnectIgViaPage={disconnectFacebook}
      showIgViaPageDisconnectConfirm={showIgViaPageDisconnectConfirm}
      onShowIgViaPageDisconnect={() => setShowIgViaPageDisconnectConfirm(true)}
      onCancelIgViaPageDisconnect={() => setShowIgViaPageDisconnectConfirm(false)}
      onConnectFacebook={connectFacebook}
      onConnectFacebookOnly={connectFacebookOnly}
      onConnectInstagram={connectInstagram}
      instagramConnectAvailable={FF_APP_INSTAGRAM_NATIVE}
      instagramConnecting={igConnecting}
      t={t}
    />
  );
};
